import { useId, useState, type FormEvent } from 'react'
import { ApiError, login, type Role, type SessionInfo } from '../../lib/api'

const COPY: Record<Role, { heading: string; hint: string }> = {
  participant: {
    heading: 'Participant sign-in',
    hint: 'Use the event password shared at check-in.',
  },
  admin: {
    heading: 'Administrator sign-in',
    hint: 'For the people running the event.',
  },
}

export default function SignInForm({ role, onSignedIn }: { role: Role; onSignedIn: (s: SessionInfo) => void }) {
  const id = useId()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      onSignedIn(await login(role, email, password))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in is unavailable right now.')
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="flex max-w-md flex-col gap-4" aria-labelledby={`${id}-heading`} noValidate>
      <h1 id={`${id}-heading`} className="text-3xl font-black tracking-tight">
        {COPY[role].heading}
      </h1>
      <p className="text-[var(--page-ink-muted)]">{COPY[role].hint}</p>

      <div className="flex flex-col gap-1">
        <label htmlFor={`${id}-email`} className="font-semibold">
          Email
        </label>
        <input
          id={`${id}-email`}
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-md border border-[var(--page-rule)] bg-[var(--page-canvas)] px-3 py-2"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor={`${id}-password`} className="font-semibold">
          Password
        </label>
        <input
          id={`${id}-password`}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="rounded-md border border-[var(--page-rule)] bg-[var(--page-canvas)] px-3 py-2"
        />
      </div>

      <div aria-live="polite" className="min-h-6">
        {error && (
          <p role="alert" className="font-semibold text-[var(--page-danger)]">
            {error}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={busy}
        className="self-start rounded-md bg-[var(--color-accent-600)] px-5 py-2.5 font-bold text-white hover:bg-[var(--color-accent-700)] disabled:opacity-60"
      >
        {busy ? 'Signing in' : 'Sign in'}
      </button>
    </form>
  )
}
